/**
 * Match comments (owner request, 2026-11-01): two members who match can talk
 * on the match page before either sends an offer, the way they can comment
 * on an offer once one exists.
 *
 * Storage is ONE document per match, matchThreads/{matchKey}, holding a
 * `messages` array that grows by arrayUnion. That mirrors how offer comments
 * live in tradeOffers.history rather than a comments collection, and it is
 * what keeps the rules simple: a party reads one doc and appends one entry,
 * no composite index, no per-row party check. A thread of 500-character
 * messages fits ~1,700 entries in the 1 MB document limit, far past any real
 * negotiation.
 *
 * `matchKey` is the server's match document ID: the two listing IDs sorted
 * (functions/matchMaker.js). The match PAGE URL is the viewer's own listing
 * first, so the same match has two URLs and one key; everything stored uses
 * the key, and links are rebuilt per recipient with matchPathFor().
 */
export const MATCH_MESSAGE_MAX_CHARS = 500;
export const MATCH_MESSAGE_MAX_IMAGES = 3;

/** The matchThreads / matches document ID for two listing IDs, in any order. */
export function matchKeyFor(listingIdA, listingIdB) {
  if (!listingIdA || !listingIdB) return null;
  const [a, b] = [String(listingIdA), String(listingIdB)].sort();
  return `${a}_${b}`;
}

/** The match page path as `viewerUid` sees it: their own listing first. */
export function matchPathFor(thread, viewerUid) {
  const pedals = (thread && thread.pedals) || {};
  const mine = pedals[viewerUid] && pedals[viewerUid].id;
  const otherUid = (thread?.participants || []).find((u) => u !== viewerUid);
  const theirs = otherUid && pedals[otherUid] && pedals[otherUid].id;
  if (!mine || !theirs) return '/matches';
  return `/match/${mine}_${theirs}`;
}

/**
 * Unread comments for `uid`, by POSITION, never by clock.
 *
 * The thread is append-only and its prefix is immutable (firestore.rules), so
 * a message's index is a server-ordered sequence number. Two read signals,
 * both indexes: `thread.lastReadCount[uid]`, the number of messages the
 * match page had loaded for this member when it stamped the marker (the rule
 * caps it at the stored length, so nobody can claim to have read a message
 * that did not exist yet), and this member's own latest message, since a
 * comment I replied after is one I have read. Everything from the other
 * member at or beyond the later of those two is unread.
 *
 * Why not timestamps: message `at` is the sender's device clock and a
 * marker would be server time, so a sender's clock running behind could make
 * a brand-new comment count as read, one running ahead could keep a read
 * comment unread for the length of the skew, and a server-time marker
 * written after loading would absorb a comment that arrived in between
 * (owner review, 2026-11-01). Indexes have none of those failure modes.
 */
export function unreadCommentCount(thread, uid) {
  const from = firstUnreadIndex(thread, uid);
  if (from < 0) return 0;
  return thread.messages.slice(from).filter((m) => m && m.from && m.from !== uid).length;
}

/**
 * Index of the first message the member has not read (the other member's
 * first message at or beyond their read position), or -1 when nothing is
 * unread. The match page scrolls here on arrival so "View Match" lands on
 * the new comments (owner, 2026-11-01).
 */
export function firstUnreadIndex(thread, uid) {
  if (!thread || !uid || !Array.isArray(thread.messages)) return -1;
  const msgs = thread.messages;
  const raw = thread.lastReadCount && thread.lastReadCount[uid];
  const readCount = Number.isInteger(raw) && raw > 0 ? Math.min(raw, msgs.length) : 0;
  let afterMyLatest = 0;
  msgs.forEach((m, i) => { if (m && m.from === uid) afterMyLatest = i + 1; });
  const from = Math.max(readCount, afterMyLatest);
  for (let i = from; i < msgs.length; i += 1) {
    if (msgs[i] && msgs[i].from && msgs[i].from !== uid) return i;
  }
  return -1;
}

/** The other participant, or null when `uid` is not in the thread. */
export function otherParticipant(thread, uid) {
  const list = (thread && thread.participants) || [];
  if (!list.includes(uid)) return null;
  return list.find((u) => u !== uid) || null;
}
